/**
 * Full-page Workflow Details (`/agent/:slug/workflows/:workflowId`) —
 * approved mockup rev 6 (U12). Steps as authored, version history with
 * Restore (rollback-as-new-version), the 7-day run history (job-triggered
 * runs show their trigger), Download (the raw WORKFLOW.md), and Delete —
 * the destructive/detail actions that deliberately do NOT exist in the
 * side panel. "Edit with Vera" returns to the console chat pre-addressed
 * (the docked split view is the follow-up iteration).
 *
 * Console chrome applies automatically (`/agent/…` route). Eligibility is
 * the same gate as the console (FR-ACCESS-1); the API is sub-scoped so a
 * foreign workflowId simply reads as missing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AGENTS } from '../../agents/registry.ts';
import { isAgentEnabledFor } from '../../agents/agentAccess.ts';
import {
  deleteWorkflow,
  getWorkflow,
  listWorkflowRuns,
  rollbackWorkflow,
  type WorkflowDetail,
  type WorkflowRunRecord,
} from '../../agents/workflowsApi.ts';
import { parseStepsForDisplay } from '../../agents/workflowMarkdown.ts';
import ConfirmDialog from '../../components/ui/ConfirmDialog.tsx';
import ChatPanel from '../../components/console/ChatPanel.tsx';
import { useAgentChat } from '../../components/console/useAgentChat.ts';
import { useSessionLog } from '../../components/console/useSessionLog.ts';
import { useSessions } from '../../components/console/useSessions.ts';
import { useAuth } from '../../auth/useAuth.ts';
import { WorkflowRunHistory, WorkflowSteps } from './WorkflowPanels.tsx';
import './WorkflowDetails.css';

type Confirming = { kind: 'delete' } | { kind: 'restore'; toVersion: number } | null;

export default function WorkflowDetails() {
  const { slug, workflowId } = useParams<{ slug: string; workflowId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<WorkflowDetail | null>(null);
  const [runs, setRuns] = useState<readonly WorkflowRunRecord[]>([]);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [busy, setBusy] = useState(false);
  // Split-view editing (approved mockup §3): the SAME console conversation,
  // docked beside the live workflow. Narrow screens keep the console
  // handoff instead (the split needs the width).
  const [editing, setEditing] = useState(false);
  const [glow, setGlow] = useState(false);

  const load = useCallback(() => {
    if (workflowId === undefined) {
      return;
    }
    void (async () => {
      try {
        const [d, r] = await Promise.all([getWorkflow(workflowId), listWorkflowRuns(workflowId)]);
        setDetail(d);
        setRuns(r);
        setMissing(false);
      } catch {
        setMissing(true);
      }
    })();
  }, [workflowId]);

  useEffect(() => {
    load();
  }, [load]);

  // Docked chat = the console's session, restored by the same hooks the
  // console uses (useSessions resumes the persisted per-agent session).
  const chatAgent = AGENTS[(slug ?? 'vera') as keyof typeof AGENTS];
  const { expireSession } = useAuth();
  const sessions = useSessions(chatAgent);
  const log = useSessionLog(sessions.currentSessionId, sessions.transcript, sessions.restoreKey);
  const priorThreads = sessions.sessions.filter((t) => t.turnCount > 0);
  const { items, toolLog, status, sendMessage, retryMessage, setFeedback } = useAgentChat(
    chatAgent,
    log,
    expireSession,
    {
      sessionId: sessions.currentSessionId,
      transcript: sessions.transcript,
      restoreKey: sessions.restoreKey,
      firstName: user?.givenName,
      returning: sessions.loading ? undefined : priorThreads.length > 0,
      loading: sessions.loading,
      creating: sessions.creating,
    },
  );

  // Vera edits land through her validating tools — when a workflow_update /
  // workflow_rollback for THIS workflow settles in the docked chat, refetch
  // and glow the version chip (the mockup's "changed in vN" moment).
  const seenToolIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    for (const entry of toolLog) {
      if (
        !entry.running &&
        (entry.name === 'workflow_update' || entry.name === 'workflow_rollback') &&
        !seenToolIds.current.has(entry.id) &&
        workflowId !== undefined &&
        (entry.request.includes(workflowId) || entry.result.includes(workflowId))
      ) {
        seenToolIds.current.add(entry.id);
        load();
        setGlow(true);
        const timer = setTimeout(() => setGlow(false), 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [toolLog, workflowId, load]);

  // Same per-user gate as the console; unknown slugs bounce to the chooser.
  if (slug === undefined || !isAgentEnabledFor(slug, user) || workflowId === undefined) {
    return <Navigate to="/app" replace />;
  }

  const consolePath = `/agent/${slug}`;

  function download(): void {
    if (detail === null) {
      return;
    }
    const blob = new Blob([detail.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${detail.workflow.workflowId}-WORKFLOW.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function confirmAction(): Promise<void> {
    if (confirming === null || detail === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (confirming.kind === 'delete') {
        await deleteWorkflow(detail.workflow.workflowId);
        navigate(consolePath, { replace: true });
        return;
      }
      await rollbackWorkflow(detail.workflow.workflowId, confirming.toVersion);
      setConfirming(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work — try again.');
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  }

  if (missing) {
    return (
      <main className="om-wfd om-wfd--missing">
        <p>That workflow doesn’t exist anymore.</p>
        <Link className="om-wfd__link" to={consolePath}>
          ‹ Back to the console
        </Link>
      </main>
    );
  }
  if (detail === null) {
    return (
      <main className="om-wfd om-wfd--missing">
        <p className="om-wfd__muted">Loading workflow…</p>
      </main>
    );
  }

  const wf = detail.workflow;
  const steps = parseStepsForDisplay(detail.markdown);

  return (
    <div className={editing ? 'om-wfd-wrap om-wfd-wrap--split' : 'om-wfd-wrap'}>
    <main className="om-wfd">
      <header className="om-wfd__top">
        <Link className="om-wfd__crumb" to={consolePath}>
          ‹ {slug} console
        </Link>
        <h1 className="om-wfd__name">{wf.displayName}</h1>
        <span className="om-wfd__chip om-wfd__chip--id">{wf.workflowId}</span>
        <span className={`om-wfd__chip om-wfd__chip--v${glow ? ' om-wfd__chip--glow' : ''}`}>v{wf.version}</span>
        <span className="om-wfd__actions">
          <button
            type="button"
            className="om-wfd__btn om-wfd__btn--primary"
            onClick={() =>
              navigate(consolePath, {
                state: {
                  autoMessage: `Run my workflow "${wf.displayName}" (${wf.workflowId}) now.`,
                },
              })
            }
            title="Run this workflow now — the run streams in chat"
          >
            Run ▸
          </button>
          <button
            type="button"
            className="om-wfd__btn"
            onClick={() => {
              if (window.matchMedia('(max-width: 900px)').matches) {
                navigate(consolePath);
              } else {
                setEditing((v) => !v);
              }
            }}
            title="Edit conversationally — the workflow refreshes as Vera saves"
          >
            {editing ? 'Close chat' : 'Edit with Vera'}
          </button>
          <button type="button" className="om-wfd__btn" onClick={download}>
            Download
          </button>
          <button
            type="button"
            className="om-wfd__btn om-wfd__btn--danger"
            onClick={() => setConfirming({ kind: 'delete' })}
          >
            Delete…
          </button>
        </span>
      </header>
      <p className="om-wfd__desc">{wf.description}</p>
      {error !== null ? (
        <p className="om-wfd__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="om-wfd__grid">
        <section className="om-wfd__panel" aria-label="Steps">
          <h2 className="om-wfd__h">Steps ({steps.length} of 10 max)</h2>
          <WorkflowSteps steps={steps} markdown={detail.markdown} />
        </section>

        <section className="om-wfd__panel" aria-label="History">
          <h2 className="om-wfd__h">Version history (kept: last 20)</h2>
          {[...detail.historyVersions].reverse().map((v) => (
            <div className="om-wfd__hrow" key={v}>
              <span className="om-wfd__hv">v{v}</span>
              <span className="om-wfd__hwhen">{v === wf.version ? 'current' : ''}</span>
              {v !== wf.version ? (
                <button
                  type="button"
                  className="om-wfd__btn om-wfd__btn--small"
                  onClick={() => setConfirming({ kind: 'restore', toVersion: v })}
                >
                  Restore…
                </button>
              ) : null}
            </div>
          ))}

          <h2 className="om-wfd__h" id="runs">
            Run history (last 7 days)
          </h2>
          <WorkflowRunHistory runs={runs} />
        </section>
      </div>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming?.kind === 'restore' ? 'Restore version' : 'Delete workflow'}
        message={
          confirming?.kind === 'restore'
            ? `Roll “${wf.displayName}” back to version ${confirming.toVersion}? Its next run will use that version's steps (restored as v${wf.version + 1}).`
            : `Delete “${wf.displayName}” and its history? This cannot be undone.`
        }
        confirmLabel={confirming?.kind === 'restore' ? 'Restore' : 'Delete'}
        danger={confirming?.kind !== 'restore'}
        busy={busy}
        onConfirm={() => {
          void confirmAction();
        }}
        onCancel={() => setConfirming(null)}
      />
    </main>
    {editing ? (
      <aside className="om-wfd-chat" aria-label="Edit with Vera">
        <ChatPanel
          agent={chatAgent}
          items={items}
          status={status}
          onSend={sendMessage}
          onRetry={retryMessage}
          onFeedback={setFeedback}
          onNewSession={sessions.newSession}
        onChooseAgent={() => navigate('/app')}
        />
      </aside>
    ) : null}
    </div>
  );
}
